import React, { useEffect, useState } from 'react';
import { ChefHat, Loader2, Clock, Check, Utensils } from 'lucide-react';
import { Card, Btn, Empty } from './shared';
import MealImage from '../nutrition/MealImage';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Key used to remember which of today's meals the user already logged, so we
 * don't let them double-count. Reset naturally when the plan is regenerated
 * (new plan id) or the date rolls over.
 */
const eatenKey = (planId, mealName) => `fff:meal-eaten:${planId}:${new Date().toISOString().slice(0, 10)}:${mealName}`;

const MealPlanCard = ({ canGenerate }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loggingName, setLoggingName] = useState(null);
  const [eaten, setEaten] = useState(new Set());
  const { toast } = useToast();

  useEffect(() => {
    api.latestMealPlan()
      .then((p) => {
        setPlan(p);
        if (p) {
          const initial = new Set();
          (p.meals || []).forEach((m) => {
            if (localStorage.getItem(eatenKey(p.id, m.name))) initial.add(m.name);
          });
          setEaten(initial);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const p = await api.generateMealPlan();
      setPlan(p);
      setEaten(new Set());
      toast({ title: 'Meal plan ready', description: `${p.meals?.length || 0} meals shaped to your targets.` });
    } catch (err) {
      toast({ title: 'Failed to generate', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const logMealEaten = async (meal) => {
    if (!meal?.name || eaten.has(meal.name)) return;
    setLoggingName(meal.name);
    try {
      await api.addManualFood({
        name: meal.name,
        portion: '1 serving',
        calories: Math.round(Number(meal.calories) || 0),
        protein_g: Math.round(Number(meal.protein_g) || 0),
        carbs_g: Math.round(Number(meal.carbs_g) || 0),
        fats_g: Math.round(Number(meal.fats_g) || 0),
      });
      const next = new Set(eaten); next.add(meal.name); setEaten(next);
      if (plan?.id) localStorage.setItem(eatenKey(plan.id, meal.name), '1');
      // Poke the DailyMacroTracker so it re-fetches today's totals.
      window.dispatchEvent(new CustomEvent('freefitfuel:food-logged'));
      toast({ title: 'Logged', description: `${meal.calories} kcal · P${meal.protein_g} added to today.` });
    } catch (err) {
      toast({ title: 'Could not log', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoggingName(null);
    }
  };

  return (
    <Card
      title="Today's meal plan" icon={ChefHat}
      action={(
        <Btn onClick={generate} disabled={!canGenerate || busy}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {plan ? 'Regenerate' : 'Build meal plan'}
        </Btn>
      )}
    >
      {!loaded ? (
        <Empty>Loading…</Empty>
      ) : !plan ? (
        <Empty>Generate a goal- and stage-driven meal plan, sized to today&apos;s calories and macros.</Empty>
      ) : (
        <div className="space-y-4">
          {plan.notes && <p className="text-[#cbb39a] italic text-sm">&ldquo;{plan.notes}&rdquo;</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {(plan.meals || []).map((m, i) => {
              const isEaten = eaten.has(m.name);
              const isLogging = loggingName === m.name;
              return (
                <div key={i} className="rounded-lg border border-[#1f2937] bg-[#0b0c10] p-4 flex flex-col gap-3" data-testid={`meal-plan-meal-${i}`}>
                  <MealImage name={m.name} hint={Array.isArray(m.ingredients) ? m.ingredients.slice(0, 3).join(', ') : ''} />
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="text-white font-bold">{m.name}</h4>
                      <p className="text-[#7c8597] text-[11px] flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{m.time}</p>
                    </div>
                    <span className="text-[#ff7a1a] font-bold text-sm whitespace-nowrap">{m.calories} kcal</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="px-2 py-0.5 rounded bg-[#1a0f06] text-[#ff7a1a] font-semibold">P {m.protein_g}g</span>
                    <span className="px-2 py-0.5 rounded bg-[#0f1115] text-[#cbd5e1] font-semibold">C {m.carbs_g}g</span>
                    <span className="px-2 py-0.5 rounded bg-[#0f1115] text-[#cbd5e1] font-semibold">F {m.fats_g}g</span>
                  </div>
                  {Array.isArray(m.ingredients) && m.ingredients.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597]">Ingredients</p>
                      <p className="text-[#aab1bf] text-xs mt-1 leading-relaxed">{m.ingredients.join(', ')}</p>
                    </div>
                  )}
                  {m.method && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597]">Method</p>
                      <p className="text-[#aab1bf] text-xs mt-1 leading-relaxed">{m.method}</p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => logMealEaten(m)}
                    disabled={isLogging || isEaten}
                    data-testid={`meal-plan-eaten-${i}`}
                    className={`mt-auto w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-wider rounded-lg py-2 border transition-colors ${
                      isEaten
                        ? 'bg-[#BEFF38]/15 border-[#BEFF38]/40 text-[#BEFF38]'
                        : 'bg-zinc-900 border-zinc-800 text-zinc-200 hover:border-[#BEFF38]/40 hover:text-[#BEFF38]'
                    } disabled:opacity-60`}
                  >
                    {isLogging ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Logging…</>
                    ) : isEaten ? (
                      <><Check className="w-3 h-3" /> Eaten · logged to today</>
                    ) : (
                      <><Utensils className="w-3 h-3" /> I ate this</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default MealPlanCard;
